import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Monitor, Mic, MicOff, Camera, CameraOff, ScreenShare, Hand,
  MessageSquare, Users, Settings, Phone, PhoneOff, Volume2, VolumeX,
  Bot, Brain, BookOpen, Lightbulb, Send, Cpu, Cog, Wrench, Zap,
  Play, ChevronRight, Star, Clock, Award, Target, Globe, Download,
  Video, Puzzle, Calculator, CheckCircle, ExternalLink, Trophy, Calendar
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const btn3D = "shadow-[0_6px_0_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_0_rgba(0,0,0,0.12),0_6px_12px_rgba(0,0,0,0.06)] hover:translate-y-[2px] active:shadow-[0_1px_0_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.04)] active:translate-y-[5px] transition-all duration-150 ease-out";

const BackBtn = ({ onClick, label = "Back" }) => (
  <button onClick={onClick}
    className={`inline-flex items-center gap-2 px-5 py-3 bg-white text-[#002147] font-semibold rounded-xl border-2 border-[#002147]/20 ${btn3D} cursor-pointer select-none`}
    data-testid="back-btn-bottom">
    <ArrowLeft size={18} /> {label}
  </button>
);

// ============================================================
// REMOTE EDUCATION PAGE
// ============================================================
const CLASSROOMS = [
  { id: "nursery", name: "Nursery", teacher: "[Teacher Name]", subject: "Early Learning", students: 18, color: "from-pink-400 to-rose-500", icon: "N" },
  { id: "kg1", name: "KG 1", teacher: "[Teacher Name]", subject: "Foundation Studies", students: 22, color: "from-orange-400 to-amber-500", icon: "K1" },
  { id: "kg2", name: "KG 2", teacher: "[Teacher Name]", subject: "Foundation Studies", students: 24, color: "from-yellow-400 to-orange-500", icon: "K2" },
  { id: "class1", name: "Class 1", teacher: "[Teacher Name]", subject: "Primary Education", students: 28, color: "from-lime-400 to-green-500", icon: "C1" },
  { id: "class2", name: "Class 2", teacher: "[Teacher Name]", subject: "Primary Education", students: 30, color: "from-green-400 to-emerald-500", icon: "C2" },
  { id: "class3", name: "Class 3", teacher: "[Teacher Name]", subject: "Primary Education", students: 27, color: "from-emerald-400 to-teal-500", icon: "C3" },
  { id: "class4", name: "Class 4", teacher: "[Teacher Name]", subject: "Upper Primary", students: 29, color: "from-teal-400 to-cyan-500", icon: "C4" },
  { id: "class5", name: "Class 5", teacher: "[Teacher Name]", subject: "Upper Primary", students: 26, color: "from-cyan-400 to-blue-500", icon: "C5" },
  { id: "class6", name: "Class 6", teacher: "[Teacher Name]", subject: "Upper Primary", students: 31, color: "from-blue-400 to-indigo-500", icon: "C6" },
  { id: "jhs1", name: "JHS 1", teacher: "[Teacher Name]", subject: "Junior High", students: 35, color: "from-indigo-400 to-violet-500", icon: "J1" },
  { id: "jhs2", name: "JHS 2", teacher: "[Teacher Name]", subject: "Junior High", students: 33, color: "from-violet-400 to-purple-500", icon: "J2" },
  { id: "jhs3", name: "JHS 3", teacher: "[Teacher Name]", subject: "Junior High", students: 32, color: "from-purple-400 to-fuchsia-500", icon: "J3" },
];

const VirtualClassroom = ({ room, onLeave }) => {
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [handRaised, setHandRaised] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { from: "System", text: `Welcome to ${room.name} Virtual Classroom!` },
    { from: room.teacher, text: `Good morning class! Today we'll be covering ${room.subject}. Let's begin.` },
  ]);
  const [chatInput, setChatInput] = useState("");

  const sendChat = () => {
    if (!chatInput.trim()) return;
    setChatMessages(prev => [...prev, { from: "You", text: chatInput }]);
    setChatInput("");
  };

  return (
    <div className="min-h-screen bg-[#0a0a1a] flex flex-col" data-testid="virtual-classroom-active">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-5 py-2.5 bg-black/70 border-b border-gray-800 z-20 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${room.color} flex items-center justify-center text-white text-xs font-bold`}>{room.icon}</div>
          <div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
              <span className="text-white font-semibold text-sm">{room.name} — Live Session</span>
            </div>
            <span className="text-white/40 text-xs">{room.teacher} &middot; {room.subject}</span>
          </div>
        </div>
        <div className="flex items-center gap-4 text-white/50 text-xs">
          <span className="flex items-center gap-1"><Users size={13} /> {room.students} in class</span>
          <span className="flex items-center gap-1"><Clock size={13} /> Live</span>
        </div>
      </div>

      {/* Main Content — large screen */}
      <div className="flex-1 flex min-h-0">
        {/* Video Area */}
        <div className="flex-1 relative bg-gradient-to-br from-[#0d1b3e] to-[#162d5a] flex items-center justify-center">
          <img src="https://images.unsplash.com/photo-1712904124132-857e6577aab9?w=1200&q=80" alt="Teacher presenting"
            className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <div className="absolute top-4 left-4 bg-black/60 px-3 py-1.5 rounded-lg text-white text-sm font-medium flex items-center gap-2">
            <div className={`w-6 h-6 rounded bg-gradient-to-br ${room.color} flex items-center justify-center text-[10px] font-bold`}>{room.icon}</div>
            {room.teacher} — {room.name}
          </div>
          <div className="absolute top-4 right-4 bg-red-600/90 px-3 py-1 rounded-full text-white text-xs font-bold flex items-center gap-1.5">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse" /> LIVE
          </div>
          {/* Small self-view */}
          <div className="absolute bottom-4 right-4 w-40 h-28 bg-[#0a0a1a] rounded-xl border-2 border-gray-700 overflow-hidden shadow-2xl">
            {camOn ? (
              <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
                <span className="text-white/50 text-2xl font-bold">You</span>
              </div>
            ) : (
              <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                <CameraOff size={24} className="text-gray-600" />
              </div>
            )}
            <div className="absolute bottom-1 left-2 text-white/60 text-[10px]">You {!micOn && "(muted)"}</div>
          </div>
          {handRaised && (
            <div className="absolute bottom-4 left-4 bg-yellow-500 text-white px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 animate-bounce">
              <Hand size={16} /> Hand Raised
            </div>
          )}
        </div>

        {/* Chat Panel */}
        {chatOpen && (
          <div className="w-80 bg-[#0f0f1a] border-l border-gray-800 flex flex-col flex-shrink-0">
            <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
              <span className="text-white font-semibold text-sm">Live Chat</span>
              <button onClick={() => setChatOpen(false)} className="text-white/40 hover:text-white text-lg">&times;</button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
              {chatMessages.map((m, i) => (
                <div key={i}>
                  <span className="font-semibold text-xs text-blue-400">{m.from}</span>
                  <p className="text-white/70 text-xs leading-relaxed">{m.text}</p>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-gray-800 flex gap-2">
              <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && sendChat()}
                className="flex-1 bg-gray-800 text-white text-sm px-3 py-2 rounded-lg border-0 focus:ring-1 focus:ring-blue-500"
                placeholder="Type a message..." data-testid="chat-input" />
              <button onClick={sendChat} className="p-2 bg-blue-600 rounded-lg text-white hover:bg-blue-700"><Send size={16} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Controls Bar */}
      <div className="flex items-center justify-center gap-4 py-4 bg-[#0a0a1a] border-t border-gray-800 flex-shrink-0">
        <button onClick={() => setMicOn(!micOn)}
          className={`w-12 h-12 rounded-full flex items-center justify-center ${micOn ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-red-600 text-white"} ${btn3D}`}
          data-testid="mic-btn">
          {micOn ? <Mic size={20} /> : <MicOff size={20} />}
        </button>
        <button onClick={() => setCamOn(!camOn)}
          className={`w-12 h-12 rounded-full flex items-center justify-center ${camOn ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-red-600 text-white"} ${btn3D}`}
          data-testid="cam-btn">
          {camOn ? <Camera size={20} /> : <CameraOff size={20} />}
        </button>
        <button className={`w-12 h-12 rounded-full bg-gray-700 text-white flex items-center justify-center hover:bg-gray-600 ${btn3D}`}
          data-testid="screen-share-btn">
          <ScreenShare size={20} />
        </button>
        <button onClick={() => setHandRaised(!handRaised)}
          className={`w-12 h-12 rounded-full flex items-center justify-center ${handRaised ? "bg-yellow-500 text-white" : "bg-gray-700 text-white hover:bg-gray-600"} ${btn3D}`}
          data-testid="hand-btn">
          <Hand size={20} />
        </button>
        <button onClick={() => setChatOpen(!chatOpen)}
          className={`w-12 h-12 rounded-full flex items-center justify-center ${chatOpen ? "bg-blue-600 text-white" : "bg-gray-700 text-white hover:bg-gray-600"} ${btn3D}`}
          data-testid="chat-btn">
          <MessageSquare size={20} />
        </button>
        <button className={`w-12 h-12 rounded-full bg-gray-700 text-white flex items-center justify-center hover:bg-gray-600 ${btn3D}`}>
          <Settings size={20} />
        </button>
        <div className="w-px h-8 bg-gray-700 mx-1" />
        <button onClick={onLeave}
          className={`px-6 h-12 rounded-full bg-red-600 text-white flex items-center justify-center gap-2 hover:bg-red-700 font-semibold text-sm ${btn3D}`}
          data-testid="leave-btn">
          <PhoneOff size={18} /> Leave Class
        </button>
      </div>
    </div>
  );
};

export const RemoteEducationPage = () => {
  const navigate = useNavigate();
  const [activeRoom, setActiveRoom] = useState(null);

  if (activeRoom) {
    return <VirtualClassroom room={activeRoom} onLeave={() => setActiveRoom(null)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="remote-education-page">
      {/* Hero */}
      <div className="bg-gradient-to-r from-[#002147] to-[#0a3d7a] text-white py-10">
        <div className="max-w-6xl mx-auto px-4">
          <button onClick={() => navigate(-1)}
            className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-4 border border-white/20 ${btn3D} cursor-pointer select-none`}
            data-testid="back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center border border-white/20">
              <Monitor size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Remote Education Centre</h1>
              <p className="text-white/70 text-sm mt-1">Every class has its own virtual classroom — teachers and students connect from anywhere</p>
            </div>
          </div>
          {/* Stats */}
          <div className="flex flex-wrap gap-4 mt-6">
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl text-sm">
              <Users size={16} className="text-green-400" />
              <span className="font-bold">12</span><span className="text-white/60">Classrooms</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl text-sm">
              <Video size={16} className="text-blue-400" />
              <span className="font-bold">Live</span><span className="text-white/60">HD Video</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl text-sm">
              <MessageSquare size={16} className="text-yellow-400" />
              <span className="font-bold">Chat</span><span className="text-white/60">& Hand Raise</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#002147]">Select Your Classroom</h2>
          <p className="text-[#002147]/60 text-sm mt-1">Each class runs independently — multiple classes can be live at the same time</p>
        </div>

        {/* Class Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="classroom-grid">
          {CLASSROOMS.map((room) => (
            <button key={room.id} onClick={() => setActiveRoom(room)}
              className={`text-left bg-white rounded-2xl overflow-hidden group cursor-pointer select-none ${btn3D}`}
              data-testid={`classroom-${room.id}`}>
              <div className={`h-24 bg-gradient-to-br ${room.color} flex items-center justify-center relative overflow-hidden`}>
                <span className="text-white/20 text-6xl font-black absolute -right-2 -bottom-2">{room.icon}</span>
                <div className="text-center z-10">
                  <p className="text-white text-2xl font-black">{room.name}</p>
                </div>
                <div className="absolute top-2 right-2 w-3 h-3 bg-green-400 rounded-full border-2 border-white/30 animate-pulse" />
              </div>
              <div className="p-4">
                <p className="font-semibold text-[#002147] text-sm truncate">{room.teacher}</p>
                <p className="text-xs text-gray-500 mt-0.5">{room.subject}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-gray-400 flex items-center gap-1"><Users size={11} /> {room.students} students</span>
                  <span className="text-xs text-green-600 font-semibold flex items-center gap-1"><Video size={11} /> Join</span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: Video, title: "HD Video", desc: "Crystal-clear live video for every classroom", color: "bg-blue-100 text-blue-600" },
            { icon: ScreenShare, title: "Screen Sharing", desc: "Teachers share presentations and resources live", color: "bg-green-100 text-green-600" },
            { icon: MessageSquare, title: "Live Chat", desc: "Students ask questions and interact in real time", color: "bg-purple-100 text-purple-600" },
            { icon: Hand, title: "Raise Hand", desc: "Signal the teacher when you have a question", color: "bg-yellow-100 text-yellow-600" },
            { icon: Users, title: "Independent Rooms", desc: "Every class runs separately with its own teacher", color: "bg-orange-100 text-orange-600" },
            { icon: Download, title: "Session Recording", desc: "Missed a class? Watch the recording later", color: "bg-red-100 text-red-600" },
          ].map((f, i) => (
            <Card key={i} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4 flex gap-3">
                <div className={`w-10 h-10 rounded-xl ${f.color} flex items-center justify-center flex-shrink-0`}><f.icon size={18} /></div>
                <div>
                  <h3 className="font-semibold text-[#002147] text-sm">{f.title}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{f.desc}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center"><BackBtn onClick={() => navigate(-1)} /></div>
      </div>
    </div>
  );
};

// ============================================================
// ROBOT TEACHER PAGE
// ============================================================
export const RobotTeacherPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [language, setLanguage] = useState("english");
  const [sessionId, setSessionId] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const audioRef = React.useRef(null);
  const mediaRecorderRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const chatEndRef = React.useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  const languages = [
    { id: "english", label: "English", flag: "EN" },
    { id: "twi", label: "Twi", flag: "TW" },
    { id: "fante", label: "Fante", flag: "FA" },
    { id: "french", label: "French", flag: "FR" },
  ];

  const subjects = [
    { id: "math", name: "Mathematics", icon: Calculator },
    { id: "science", name: "Science", icon: Zap },
    { id: "english", name: "English", icon: BookOpen },
    { id: "ict", name: "ICT & Coding", icon: Cpu },
  ];

  React.useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* ======= SEND TEXT MESSAGE ======= */
  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { from: "user", text: msg }]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/robot-teacher/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, language, session_id: sessionId }),
      });
      const data = await res.json();
      if (!sessionId && data.session_id) setSessionId(data.session_id);
      setMessages((prev) => [...prev, { from: "robot", text: data.text }]);

      if (data.audio_base64) {
        const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
        audioRef.current = audio;
        audio.onplay = () => setIsSpeaking(true);
        audio.onended = () => setIsSpeaking(false);
        audio.onpause = () => setIsSpeaking(false);
        audio.onerror = () => setIsSpeaking(false);
        audio.play().catch(() => setIsSpeaking(false));
      }
    } catch (err) {
      setMessages((prev) => [...prev, { from: "robot", text: "Oops! I'm having trouble connecting. Please try again." }]);
    }
    setLoading(false);
  };

  /* ======= MICROPHONE RECORDING ======= */
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick best supported mime type
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : "";
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType || "audio/webm" });
        if (blob.size > 0) {
          await transcribeAndSend(blob);
        }
      };

      mediaRecorder.start(250); // collect data every 250ms
      setIsRecording(true);
    } catch (err) {
      console.error("Mic error:", err);
      setMessages((prev) => [...prev, { from: "robot", text: "I couldn't access your microphone. Please allow microphone access in your browser and try again." }]);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAndSend = async (blob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append("audio", blob, "recording.webm");
      formData.append("language", language);

      const res = await fetch(`${API}/api/robot-teacher/transcribe`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.text && data.text.trim()) {
        setIsTranscribing(false);
        await sendMessage(data.text.trim());
      } else {
        setIsTranscribing(false);
        setMessages((prev) => [...prev, { from: "robot", text: "I couldn't hear that clearly. Could you try again?" }]);
      }
    } catch (err) {
      setIsTranscribing(false);
      setMessages((prev) => [...prev, { from: "robot", text: "Sorry, I had trouble hearing you. Please try again." }]);
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsSpeaking(false);
    }
  };

  const resetChat = () => {
    setMessages([]);
    setSessionId("");
    stopSpeaking();
    fetch(`${API}/api/robot-teacher/reset`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sessionId }) }).catch(() => {});
  };

  /* ======= SVG ROBOT (MR. OSEI) ======= */
  const RobotAvatar = ({ speaking, recording }) => (
    <svg viewBox="0 0 200 240" className="w-full h-full" data-testid="robot-avatar">
      {/* Antenna */}
      <line x1="100" y1="12" x2="100" y2="36" stroke="#FFD700" strokeWidth="4" strokeLinecap="round"/>
      <circle cx="100" cy="8" r="7" fill="#FFD700">
        {(speaking || recording) && <animate attributeName="r" values="7;10;7" dur="0.6s" repeatCount="indefinite"/>}
      </circle>
      {(speaking || recording) && <circle cx="100" cy="8" r="14" fill="none" stroke={recording ? "#EF4444" : "#FFD700"} strokeWidth="1" opacity="0.4">
        <animate attributeName="r" values="14;22;14" dur="1s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.4;0;0.4" dur="1s" repeatCount="indefinite"/>
      </circle>}

      {/* Head */}
      <rect x="40" y="36" width="120" height="100" rx="24" fill="#1E88E5" stroke="#1565C0" strokeWidth="3"/>
      <rect x="48" y="44" width="104" height="84" rx="18" fill="#0D47A1" opacity="0.3"/>

      {/* Ears (listening indicators) */}
      <rect x="26" y="68" width="14" height="24" rx="7" fill={recording ? "#EF4444" : "#42A5F5"}>
        {recording && <animate attributeName="fill" values="#EF4444;#FF8888;#EF4444" dur="0.6s" repeatCount="indefinite"/>}
      </rect>
      <rect x="160" y="68" width="14" height="24" rx="7" fill={recording ? "#EF4444" : "#42A5F5"}>
        {recording && <animate attributeName="fill" values="#EF4444;#FF8888;#EF4444" dur="0.6s" repeatCount="indefinite"/>}
      </rect>

      {/* Eyes */}
      <ellipse cx="72" cy="76" rx="18" ry="20" fill="white"/>
      <ellipse cx="128" cy="76" rx="18" ry="20" fill="white"/>
      <circle cx="72" cy="76" r="9" fill="#0D47A1">
        {speaking && <animate attributeName="cy" values="76;74;78;76" dur="2s" repeatCount="indefinite"/>}
      </circle>
      <circle cx="128" cy="76" r="9" fill="#0D47A1">
        {speaking && <animate attributeName="cy" values="76;78;74;76" dur="2s" repeatCount="indefinite"/>}
      </circle>
      <circle cx="75" cy="73" r="3" fill="white" opacity="0.8"/>
      <circle cx="131" cy="73" r="3" fill="white" opacity="0.8"/>

      {/* Eyebrows */}
      <rect x="54" y="54" width="36" height="5" rx="3" fill="#FFD700"/>
      <rect x="110" y="54" width="36" height="5" rx="3" fill="#FFD700"/>

      {/* Mouth */}
      {speaking ? (
        <ellipse cx="100" cy="112" rx="22" fill="#FF6B00">
          <animate attributeName="ry" values="6;14;8;16;6" dur="0.4s" repeatCount="indefinite"/>
        </ellipse>
      ) : recording ? (
        <ellipse cx="100" cy="112" rx="16" ry="10" fill="#EF4444" opacity="0.8">
          <animate attributeName="rx" values="16;20;16" dur="0.8s" repeatCount="indefinite"/>
        </ellipse>
      ) : (
        <path d="M78 108 Q100 124 122 108" stroke="#FFD700" strokeWidth="4" fill="none" strokeLinecap="round"/>
      )}

      {/* Cheeks */}
      <circle cx="50" cy="100" r="8" fill="#FF6B00" opacity="0.3"/>
      <circle cx="150" cy="100" r="8" fill="#FF6B00" opacity="0.3"/>

      {/* Neck */}
      <rect x="88" y="136" width="24" height="14" rx="4" fill="#90CAF9"/>

      {/* Body */}
      <rect x="50" y="150" width="100" height="70" rx="18" fill="#1E88E5" stroke="#1565C0" strokeWidth="3"/>
      <rect x="70" y="162" width="60" height="28" rx="8" fill="#0D47A1" opacity="0.4"/>
      <circle cx="100" cy="176" r="8" fill="#FF6B00">
        {speaking && <animate attributeName="opacity" values="1;0.4;1" dur="0.8s" repeatCount="indefinite"/>}
        {recording && <animate attributeName="fill" values="#FF6B00;#EF4444;#FF6B00" dur="0.5s" repeatCount="indefinite"/>}
      </circle>

      {/* Arms */}
      <rect x="20" y="155" width="28" height="50" rx="14" fill="#42A5F5"/>
      <rect x="152" y="155" width="28" height="50" rx="14" fill="#42A5F5"/>

      {/* Sound waves when speaking */}
      {speaking && <>
        <path d="M168 70 Q178 76 168 82" stroke="#FFD700" strokeWidth="2" fill="none" opacity="0.6">
          <animate attributeName="opacity" values="0.6;0;0.6" dur="0.5s" repeatCount="indefinite"/>
        </path>
        <path d="M176 64 Q190 76 176 88" stroke="#FFD700" strokeWidth="2" fill="none" opacity="0.4">
          <animate attributeName="opacity" values="0;0.4;0" dur="0.5s" repeatCount="indefinite"/>
        </path>
        <path d="M32 70 Q22 76 32 82" stroke="#FFD700" strokeWidth="2" fill="none" opacity="0.6">
          <animate attributeName="opacity" values="0.6;0;0.6" dur="0.5s" repeatCount="indefinite"/>
        </path>
        <path d="M24 64 Q10 76 24 88" stroke="#FFD700" strokeWidth="2" fill="none" opacity="0.4">
          <animate attributeName="opacity" values="0;0.4;0" dur="0.5s" repeatCount="indefinite"/>
        </path>
      </>}

      {/* Listening waves when recording */}
      {recording && <>
        <path d="M22 74 Q12 80 22 86" stroke="#EF4444" strokeWidth="2" fill="none">
          <animate attributeName="opacity" values="1;0;1" dur="0.6s" repeatCount="indefinite"/>
        </path>
        <path d="M178 74 Q188 80 178 86" stroke="#EF4444" strokeWidth="2" fill="none">
          <animate attributeName="opacity" values="1;0;1" dur="0.6s" repeatCount="indefinite"/>
        </path>
      </>}
    </svg>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a1628] to-[#0d2040]" data-testid="robot-teacher-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-700 to-blue-900 text-white py-4">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)}
            className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl border border-white/20 ${btn3D} cursor-pointer select-none`}
            data-testid="back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <h1 className="text-lg font-bold flex items-center gap-2"><Bot size={22} /> Robo-Teach</h1>
          <button onClick={resetChat} className="text-xs text-white/50 hover:text-white/80 transition-colors" data-testid="reset-chat-btn">Reset Chat</button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-[320px_1fr] gap-6">

          {/* Left: Robot + Language */}
          <div className="space-y-4">
            {/* Robot */}
            <div className="bg-gradient-to-b from-[#1a2744] to-[#0d1b30] rounded-3xl p-6 border border-white/10 shadow-2xl" data-testid="robot-container">
              <div className="w-48 h-56 mx-auto mb-4">
                <RobotAvatar speaking={isSpeaking} recording={isRecording} />
              </div>
              <div className="text-center">
                <p className="text-white font-bold text-lg">Robo-Teach</p>
                <div className="flex items-center justify-center gap-2 mt-1">
                  <div className={`w-2.5 h-2.5 rounded-full ${isRecording ? "bg-red-500 animate-pulse" : isSpeaking ? "bg-green-400 animate-pulse" : loading ? "bg-yellow-400 animate-pulse" : isTranscribing ? "bg-blue-400 animate-pulse" : "bg-green-400"}`} />
                  <span className="text-white/50 text-xs">{isRecording ? "Listening..." : isSpeaking ? "Speaking..." : loading ? "Thinking..." : isTranscribing ? "Processing..." : "Online"}</span>
                </div>
                <div className="flex items-center justify-center gap-2 mt-3">
                  {isSpeaking && (
                    <button onClick={stopSpeaking} className="px-4 py-1.5 bg-red-500/20 text-red-300 text-xs rounded-lg hover:bg-red-500/30 transition-colors border border-red-500/30" data-testid="stop-speaking-btn">
                      <VolumeX size={12} className="inline mr-1" /> Stop
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Language Selector */}
            <div className="bg-[#1a2744] rounded-2xl p-4 border border-white/10">
              <p className="text-white/50 text-xs font-bold uppercase tracking-wider mb-3">Speak in</p>
              <div className="grid grid-cols-2 gap-2">
                {languages.map((l) => (
                  <button key={l.id} onClick={() => setLanguage(l.id)}
                    className={`px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                      language === l.id
                        ? "bg-[#FF6B00] text-white shadow-lg"
                        : "bg-white/5 text-white/60 hover:bg-white/10 border border-white/10"
                    } ${btn3D}`}
                    data-testid={`lang-${l.id}`}>
                    <span className="text-[10px] font-bold opacity-60 block">{l.flag}</span>
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Subjects */}
            <div className="bg-[#1a2744] rounded-2xl p-4 border border-white/10">
              <p className="text-white/50 text-xs font-bold uppercase tracking-wider mb-3">Quick Topics</p>
              <div className="space-y-2">
                {subjects.map((s) => (
                  <button key={s.id} onClick={() => sendMessage(`Teach me ${s.name}`)}
                    className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium text-white/80 bg-white/5 hover:bg-white/10 border border-white/10 flex items-center gap-2 ${btn3D}`}
                    data-testid={`topic-${s.id}`}>
                    <s.icon size={16} className="text-cyan-400" /> {s.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Chat */}
          <div className="bg-[#1a2744] rounded-3xl border border-white/10 shadow-2xl flex flex-col overflow-hidden" style={{ minHeight: "600px" }} data-testid="robot-chat">
            <div className="px-5 py-3 bg-black/30 flex items-center gap-2 border-b border-white/10">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
              <span className="text-white/70 text-xs font-medium">Robo-Teach — {languages.find((l) => l.id === language)?.label}</span>
              <Badge className="bg-cyan-500/20 text-cyan-300 text-xs ml-auto">AI + Voice</Badge>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4" style={{ maxHeight: "430px" }}>
              {messages.length === 0 && (
                <div className="text-center py-12">
                  <Bot size={48} className="mx-auto text-white/10 mb-4" />
                  <p className="text-white/30 text-sm font-medium">Say hello or press the microphone to talk to Robo-Teach!</p>
                  <p className="text-white/20 text-xs mt-1">I'll respond with voice in {languages.find((l) => l.id === language)?.label}</p>
                  <p className="text-cyan-400/40 text-xs mt-3 flex items-center justify-center gap-1"><Mic size={12} /> Hold the mic button to talk</p>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  {m.from === "robot" && (
                    <div className="w-8 h-8 rounded-full bg-cyan-600 flex items-center justify-center mr-2 flex-shrink-0 mt-1">
                      <Bot size={16} className="text-white" />
                    </div>
                  )}
                  <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                    m.from === "user"
                      ? "bg-[#FF6B00] text-white rounded-br-md"
                      : "bg-white/10 text-white/90 rounded-bl-md border border-white/10"
                  }`}>
                    {m.from === "user" && m.voice && <Mic size={12} className="inline mr-1 opacity-60" />}
                    {m.text}
                  </div>
                </div>
              ))}
              {(loading || isTranscribing) && (
                <div className="flex justify-start">
                  <div className="w-8 h-8 rounded-full bg-cyan-600 flex items-center justify-center mr-2 flex-shrink-0">
                    <Bot size={16} className="text-white" />
                  </div>
                  <div className="bg-white/10 text-white/50 px-4 py-3 rounded-2xl rounded-bl-md border border-white/10 text-sm flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                    {isTranscribing ? "Hearing you..." : "Thinking..."}
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input area with mic */}
            <div className="p-4 bg-black/30 border-t border-white/10">
              {/* Microphone button - big and prominent */}
              <div className="flex justify-center mb-3">
                <button
                  onMouseDown={startRecording}
                  onMouseUp={stopRecording}
                  onMouseLeave={isRecording ? stopRecording : undefined}
                  onTouchStart={startRecording}
                  onTouchEnd={stopRecording}
                  disabled={loading || isSpeaking || isTranscribing}
                  className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200 disabled:opacity-30 ${
                    isRecording
                      ? "bg-red-500 scale-110 shadow-[0_0_24px_rgba(239,68,68,0.5)]"
                      : "bg-cyan-600 hover:bg-cyan-500 shadow-[0_6px_0_rgba(0,0,0,0.2)] hover:shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px] active:shadow-none active:translate-y-[6px]"
                  }`}
                  data-testid="mic-btn"
                >
                  {isRecording ? (
                    <div className="relative">
                      <Mic size={26} className="text-white" />
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full animate-ping" />
                    </div>
                  ) : (
                    <Mic size={26} className="text-white" />
                  )}
                </button>
              </div>
              <p className="text-center text-white/30 text-[10px] mb-3">
                {isRecording ? "Release to send..." : "Hold to talk to Robo-Teach"}
              </p>

              {/* Text input fallback */}
              <div className="flex gap-3">
                <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                  className="flex-1 bg-white/10 text-white text-sm px-5 py-3 rounded-xl border border-white/10 focus:ring-2 focus:ring-cyan-500 focus:outline-none placeholder-white/30"
                  placeholder={`Or type your question...`}
                  disabled={loading || isRecording || isTranscribing}
                  data-testid="robot-input" />
                <button onClick={() => sendMessage()} disabled={loading || isRecording || isTranscribing}
                  className={`px-6 py-3 bg-[#FF6B00] text-white font-bold rounded-xl ${btn3D} flex items-center gap-2 disabled:opacity-40`}
                  data-testid="robot-send-btn">
                  <Send size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// ROBOTICS PAGE
// ============================================================
export const RoboticsPage = () => {
  const navigate = useNavigate();
  const [selectedLevel, setSelectedLevel] = useState("all");

  const levels = ["all", "Beginner", "Intermediate", "Advanced"];

  const tutorials = [
    { title: "What is a Robot?", desc: "Introduction to robotics - learn what robots are, how they work, and where they are used in everyday life.", level: "Beginner", duration: "10 min", category: "Theory", image: "https://images.pexels.com/photos/8566458/pexels-photo-8566458.jpeg?auto=compress&w=400" },
    { title: "Build Your First Circuit", desc: "Learn about electrical circuits using LEDs, batteries, and switches. Hands-on project included!", level: "Beginner", duration: "25 min", category: "Electronics", image: "https://images.pexels.com/photos/7869085/pexels-photo-7869085.jpeg?auto=compress&w=400" },
    { title: "Programming with Scratch", desc: "Use Scratch to program virtual robots. Learn loops, conditionals, and events through fun challenges.", level: "Beginner", duration: "30 min", category: "Coding", image: "https://images.pexels.com/photos/7869087/pexels-photo-7869087.jpeg?auto=compress&w=400" },
    { title: "Sensors & Input Devices", desc: "Explore ultrasonic, infrared, and touch sensors. Learn how robots 'see' and 'feel' the world.", level: "Intermediate", duration: "20 min", category: "Electronics", image: "https://images.unsplash.com/photo-1769839272014-089cd3bbc274?w=400&q=60" },
    { title: "Arduino Basics", desc: "Introduction to Arduino microcontroller. Write your first program to blink an LED and read sensor data.", level: "Intermediate", duration: "35 min", category: "Coding", image: "https://images.pexels.com/photos/7869085/pexels-photo-7869085.jpeg?auto=compress&w=400" },
    { title: "Line-Following Robot", desc: "Build and program a robot that follows a black line on the ground using infrared sensors.", level: "Intermediate", duration: "45 min", category: "Project", image: "https://images.unsplash.com/photo-1769839272014-089cd3bbc274?w=400&q=60" },
    { title: "Obstacle Avoidance Robot", desc: "Create a robot that detects and navigates around obstacles using ultrasonic sensors and servo motors.", level: "Advanced", duration: "50 min", category: "Project", image: "https://images.pexels.com/photos/7869087/pexels-photo-7869087.jpeg?auto=compress&w=400" },
    { title: "Python for Robotics", desc: "Learn Python programming for Raspberry Pi robotics. Control motors, sensors, and cameras with code.", level: "Advanced", duration: "40 min", category: "Coding", image: "https://images.pexels.com/photos/7869085/pexels-photo-7869085.jpeg?auto=compress&w=400" },
    { title: "AI & Machine Learning Basics", desc: "Introduction to artificial intelligence. Train a simple image recognition model for your robot.", level: "Advanced", duration: "45 min", category: "AI", image: "https://images.pexels.com/photos/8566521/pexels-photo-8566521.jpeg?auto=compress&w=400" },
  ];

  const resources = [
    { title: "Arduino Official Tutorials", url: "https://www.arduino.cc/en/Tutorial/HomePage", icon: Cpu },
    { title: "Scratch Programming", url: "https://scratch.mit.edu/", icon: Puzzle },
    { title: "Tinkercad Circuits Simulator", url: "https://www.tinkercad.com/circuits", icon: Zap },
    { title: "Khan Academy Computing", url: "https://www.khanacademy.org/computing", icon: Brain },
    { title: "Code.org - Learn to Code", url: "https://code.org/", icon: Cpu },
    { title: "Raspberry Pi Projects", url: "https://projects.raspberrypi.org/en", icon: Cog },
  ];

  const competitions = [
    { name: "YCPS Internal Robotics Challenge", date: "May 2026", desc: "Build a robot that completes an obstacle course. Open to all club members.", status: "Upcoming" },
    { name: "Western Region STEM Robotics Fair", date: "July 2026", desc: "Represent YCPS at the regional robotics exhibition and competition.", status: "Registration Open" },
    { name: "Ghana National Robotics Olympiad", date: "October 2026", desc: "The ultimate robotics competition for schools across Ghana.", status: "Qualifying" },
  ];

  const filtered = selectedLevel === "all" ? tutorials : tutorials.filter(t => t.level === selectedLevel);

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="robotics-page">
      <div className="bg-gradient-to-r from-violet-700 to-purple-800 text-white py-8">
        <div className="max-w-6xl mx-auto px-4">
          <button onClick={() => navigate(-1)}
            className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-3 border border-white/20 ${btn3D} cursor-pointer select-none`}
            data-testid="back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center"><Cog size={28} /></div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Robotics Academy</h1>
              <p className="text-white/80 text-sm">Build, Code, Create - The Future of Technology</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Hero Banner */}
        <Card className="overflow-hidden shadow-2xl border-0" data-testid="robotics-hero">
          <div className="relative h-64 bg-gradient-to-br from-violet-900 to-purple-950">
            <img src="https://images.pexels.com/photos/7869085/pexels-photo-7869085.jpeg?auto=compress&w=800" alt="Robotics" className="w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-r from-violet-900/80 to-transparent" />
            <div className="absolute inset-0 flex items-center px-8">
              <div className="max-w-lg">
                <Badge className="bg-violet-500/30 text-white mb-3">YCPS Robotics Programme</Badge>
                <h2 className="text-2xl font-bold text-white mb-2">Learn to Build Robots from Scratch</h2>
                <p className="text-white/70 text-sm mb-4">From basic circuits to AI-powered machines. Step-by-step tutorials, hands-on projects, and competitions for every skill level.</p>
                <button onClick={() => navigate("/school-clubs/ict-coding-club")}
                  className={`px-6 py-3 bg-[#FF6B00] text-white font-bold rounded-xl ${btn3D} flex items-center gap-2`}
                  data-testid="join-robotics-btn">
                  <Wrench size={18} /> Join Robotics Club
                </button>
              </div>
            </div>
          </div>
        </Card>

        {/* Filter */}
        <div className="flex flex-wrap gap-2">
          {levels.map(l => (
            <button key={l} onClick={() => setSelectedLevel(l)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium ${selectedLevel === l ? "bg-[#002147] text-white" : "bg-white/80 text-[#002147] hover:bg-white"} ${btn3D}`}
              data-testid={`level-${l}`}>
              {l === "all" ? "All Levels" : l}
            </button>
          ))}
        </div>

        {/* Tutorials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((t, i) => (
            <Card key={i} className="overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer group" data-testid={`tutorial-${i}`}>
              <div className="h-36 overflow-hidden relative">
                <img src={t.image} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <Badge className="absolute top-3 right-3 bg-white/20 text-white">{t.category}</Badge>
                <div className="absolute bottom-3 left-3 flex gap-2">
                  <Badge className={`text-xs ${t.level === "Beginner" ? "bg-green-500" : t.level === "Intermediate" ? "bg-yellow-500" : "bg-red-500"} text-white`}>{t.level}</Badge>
                  <Badge className="bg-black/40 text-white text-xs flex items-center gap-1"><Clock size={10} /> {t.duration}</Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="font-bold text-[#002147] mb-1">{t.title}</h3>
                <p className="text-sm text-gray-600 mb-2">{t.desc}</p>
                <p className="text-xs text-[#FF6B00] font-semibold flex items-center gap-1">Start Tutorial <ChevronRight size={12} /></p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Resources */}
        <h2 className="text-xl font-bold text-[#002147] flex items-center gap-2"><Globe size={22} /> Learning Resources</h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {resources.map((r, i) => (
            <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
              className={`bg-white rounded-xl p-4 flex items-center gap-3 ${btn3D} cursor-pointer select-none`}
              data-testid={`resource-${i}`}>
              <div className="w-10 h-10 bg-violet-100 text-violet-600 rounded-lg flex items-center justify-center flex-shrink-0"><r.icon size={18} /></div>
              <div>
                <h3 className="font-semibold text-[#002147] text-sm">{r.title}</h3>
                <p className="text-xs text-[#FF6B00] flex items-center gap-1">Visit <ExternalLink size={10} /></p>
              </div>
            </a>
          ))}
        </div>

        {/* Competitions */}
        <h2 className="text-xl font-bold text-[#002147] flex items-center gap-2"><Trophy size={22} /> Competitions & Events</h2>
        <div className="space-y-3">
          {competitions.map((c, i) => (
            <Card key={i} className="hover:shadow-lg transition-shadow" data-testid={`competition-${i}`}>
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center text-white flex-shrink-0 shadow-md">
                  <Trophy size={24} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-[#002147]">{c.name}</h3>
                    <Badge className={`text-xs ${c.status === "Upcoming" ? "bg-blue-100 text-blue-700" : c.status === "Registration Open" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`}>{c.status}</Badge>
                  </div>
                  <p className="text-sm text-gray-600">{c.desc}</p>
                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><Calendar size={10} /> {c.date}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-4"><BackBtn onClick={() => navigate(-1)} /></div>
      </div>
    </div>
  );
};
